import type { RequestOptions } from 'https';
import type { BuiltLocalProxyHttpRequest } from './build-local-proxy-http-request.js';
import { LOCAL_PROXY_REQUEST_TIMEOUT_MS } from './constants.js';
import { incomingMessageHeaders } from './incoming-message-headers.js';

export async function loadHttpModule(
  isHttps: boolean,
): Promise<typeof import('http') | typeof import('https')> {
  return isHttps ? import('https') : import('http');
}

export function buildLocalHttpRequestOptions(built: BuiltLocalProxyHttpRequest): RequestOptions {
  return {
    method: built.method,
    hostname: built.hostname,
    port: built.port,
    path: built.path,
    headers: built.headers,
  };
}

export function runLocalHttpRequestOnce(
  mod: typeof import('http') | typeof import('https'),
  built: BuiltLocalProxyHttpRequest,
): Promise<{
  statusCode: number;
  headers: Record<string, string>;
  body: Buffer;
  error?: string;
}> {
  const opts = buildLocalHttpRequestOptions(built);

  return new Promise((resolve) => {
    const req = mod.request(opts, (res) => {
      const chunks: Buffer[] = [];
      res.on('data', (chunk) => chunks.push(chunk));
      res.on('end', () => {
        resolve({
          statusCode: res.statusCode ?? 0,
          headers: incomingMessageHeaders(res),
          body: Buffer.concat(chunks),
        });
      });
      res.on('error', (err) => {
        resolve({
          statusCode: 0,
          headers: {},
          body: Buffer.alloc(0),
          error: err.message,
        });
      });
    });

    req.setTimeout(LOCAL_PROXY_REQUEST_TIMEOUT_MS, () => {
      req.destroy(new Error(`Local preview request timed out after ${LOCAL_PROXY_REQUEST_TIMEOUT_MS}ms`));
    });

    req.on('error', (err) => {
      resolve({
        statusCode: 0,
        headers: {},
        body: Buffer.alloc(0),
        error: err.message,
      });
    });

    if (built.sendingBody && built.body != null) {
      req.write(built.body);
    }
    req.end();
  });
}
